import { useCallback, useEffect, useMemo } from "react";
import { formRedirectToUrl, isRelativeUrl } from "../navigationHelpers";
import { useSessionStorage } from "./useSessionStorage";

export const REDIRECT_TO_QUERY_KEY = "redirect_to";

// useSessionRedirect wraps the logic for redirect_to
//
// decode to ensure if the original data was encoded to being with, so we're dealing with raw data.
// re-encode if we're passing it through as a param. This is because if the original data was
// already encoded, we don't want to double encode it.
//
// As a function of this hook, when you retrieve the session redirect or the encoded session redirect you delete the
// current value, this is its standard usage pattern.
export const useSessionRedirect = () => {
  const [value, setValue, removeValue] = useSessionStorage<string>(REDIRECT_TO_QUERY_KEY, "");
  useEffect(() => {
    if (value && shouldIgnore(value)) {
      removeValue();
    }
  }, [value, removeValue]);

  const decodedRedirectTo = safeDecodeURIComponent(value ?? "");
  const addSessionRedirect = useCallback(
    (route: string): string => {
      let redirectUrl;
      if (isRelativeUrl(decodedRedirectTo)) {
        redirectUrl = decodedRedirectTo ? joinPath(route, decodedRedirectTo) : route;
      } else {
        redirectUrl = formRedirectToUrl(decodedRedirectTo);
      }
      removeValue();
      return redirectUrl === "/" ? route : redirectUrl;
    },
    [decodedRedirectTo, removeValue]
  );

  const encodeSessionRedirect = useCallback(
    (route: string): string => {
      const encodedValue = encodeURIComponent(decodedRedirectTo);
      route = decodedRedirectTo ? joinPath(route, `?redirect_to=${encodedValue}`) : route;
      removeValue();
      return route;
    },
    [decodedRedirectTo, removeValue]
  );

  return useMemo(() => {
    return {
      addSessionRedirect,
      encodeSessionRedirect,
    };
  }, [addSessionRedirect, encodeSessionRedirect]);
};

/**
 * This joins the path of a URL and a fragment.
 * There doesn't appear to be a standardized way to do this in JS whereas there is in Node.
 * @param baseUrl
 * @param fragment
 */
function joinPath(baseUrl: string, fragment: string) {
  if (baseUrl.endsWith("/") && fragment.startsWith("/")) {
    return baseUrl.concat(fragment.slice(1));
  }
  if (baseUrl.endsWith("?") && fragment.startsWith("?")) {
    return baseUrl.concat(fragment.slice(1));
  }
  return baseUrl.concat(fragment);
}

const ignoreList = ["/logout", "/"];

/**
 * Tests should I ignore this redirectTo, list is defined in <code>ignoreList</code>
 * @param redirectTo the path
 */
function shouldIgnore(redirectTo: string) {
  for (const item of ignoreList) {
    if (normalizeCompare(redirectTo, item)) {
      return true;
    }
  }
  return false;
}

function normalizeCompare(x: string, y: string) {
  return x === y || safeDecodeURIComponent(x) === safeDecodeURIComponent(y);
}

// decodeURIComponent throws URIError on malformed input (e.g. a bare "%"). The redirect_to value
// now comes straight from sessionStorage (index.html no longer decodes it at write time), so a
// malformed value can reach here; guard against it to avoid crashing the render tree (there is no
// error boundary). See INC-18719 follow-up review (PR #253).
function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed redirect_to (e.g. a bare "%") is expected client-supplied input; treat it as "no
    // redirect" rather than letting the URIError crash the render tree (there is no error boundary).
    // Warn — not error — so it's visible for debugging without adding non-actionable noise to
    // Datadog RUM error monitoring for what is just malformed user input.
    console.warn("useSessionRedirect: ignoring redirect_to value that could not be URI-decoded");
    return "";
  }
}
